import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, Search, RefreshCw, ChevronRight, MessageCircle } from "lucide-react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import OrderChatModal from "@/components/OrderChatModal";
import { toast } from "sonner";

const STATUS_COLOR = {
  NEW: "bg-blue-100 text-blue-700", ACCEPTED: "bg-blue-100 text-blue-700",
  PREPARING: "bg-amber-100 text-amber-700", READY: "bg-purple-100 text-purple-700",
  OUT_FOR_DELIVERY: "bg-indigo-100 text-indigo-700", COMPLETED: "bg-green-100 text-green-700",
  CANCELLED: "bg-red-100 text-red-700",
};

export default function Orders() {
  const { user, loading } = useAuth();
  const { addItem, setOpen } = useCart();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [guestOrders, setGuestOrders] = useState([]);
  const [track, setTrack] = useState("");
  const [chatOrder, setChatOrder] = useState(null);

  useEffect(() => {
    if (user) { api.get("/account/orders").then(({ data }) => setOrders(data)).catch(() => {}); return; }
    try {
      // Guests only see their single most recent order placed on this device
      const n = localStorage.getItem("metros_last_order");
      if (n) api.get(`/orders/${n}`).then((r) => setGuestOrders([r.data])).catch(() => setGuestOrders([]));
      else setGuestOrders([]);
    } catch (e) { console.warn("Failed to load guest order", e); }
  }, [user]);

  const reorder = async (order) => {
    let added = 0, skipped = 0;
    for (const it of order.items) {
      const prod = await api.get(`/menu/products/${it.product_id}`).then((r) => r.data).catch(() => null);
      if (!prod || !prod.available) { skipped++; continue; }
      const validIds = new Set((prod.modifier_groups || []).flatMap((g) => g.options.map((o) => o.id)));
      const opts = (it.options || []).filter((o) => validIds.has(o.id)).map((o) => ({ id: o.id, name: o.name, price: o.price, group: o.group }));
      addItem({ product_id: prod.id, name: prod.name, image: prod.image, price: prod.price, quantity: it.quantity, selected_options: opts, special_instructions: it.special_instructions || "" });
      added++;
    }
    if (added) toast.success(`${added} item${added > 1 ? "s" : ""} added${skipped ? ` · ${skipped} unavailable` : ""}`);
    else toast.error("Those items aren't available right now");
    if (added) setOpen(true);
  };

  return (
    <div className="max-w-3xl mx-auto px-4 md:px-6 py-8">
      <h1 className="font-heading font-extrabold text-3xl mb-5">Your Orders</h1>

      <div className="bg-white border border-gray-100 rounded-2xl p-4 mb-6">
        <p className="text-sm font-semibold mb-2">Track any order</p>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input data-testid="track-input" value={track} onChange={(e) => setTrack(e.target.value.toUpperCase())} placeholder="Order number e.g. MET-123456" className="w-full bg-gray-100 rounded-full pl-9 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-metros-red focus:outline-none" />
          </div>
          <button data-testid="track-btn" onClick={() => track && navigate(`/order/${track}`)} className="bg-metros-black text-white rounded-full px-5 text-sm font-semibold">Track</button>
        </div>
      </div>

      {loading ? null : (user ? orders : guestOrders).length > 0 ? (
        <div className="space-y-3">
          {!user && <p className="text-xs text-muted-foreground -mt-2 mb-1">Your most recent order on this device</p>}
          {(user ? orders : guestOrders).map((o) => (
            <div key={o.id} data-testid={`order-row-${o.order_number}`} className="bg-white border border-gray-100 rounded-2xl p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-heading font-bold">{o.order_number}</p>
                  <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()} · {o.type}</p>
                </div>
                <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_COLOR[o.status]}`}>{o.status.replace(/_/g, " ")}</span>
              </div>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-1">{o.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</p>
              <div className="flex items-center justify-between mt-3">
                <span className="font-heading font-bold">{money(o.total)}</span>
                <div className="flex gap-2">
                  <button data-testid={`reorder-${o.order_number}`} onClick={() => reorder(o)} className="flex items-center gap-1 text-sm font-semibold border border-gray-200 rounded-full px-4 py-2 hover:border-metros-red"><RefreshCw size={14} /> Reorder</button>
                  {user && <button data-testid={`order-chat-btn-${o.order_number}`} onClick={() => setChatOrder(o)} className="flex items-center gap-1 text-sm font-semibold border border-gray-200 rounded-full px-4 py-2 hover:border-metros-red"><MessageCircle size={14} /> Chat</button>}
                  <button onClick={() => navigate(`/order/${o.order_number}`)} className="flex items-center gap-1 text-sm font-semibold bg-metros-black text-white rounded-full px-4 py-2">Track <ChevronRight size={14} /></button>
                </div>
              </div>
            </div>
          ))}
          {!user && (
            <div className="text-center pt-4">
              <p className="text-sm text-muted-foreground">Sign in to keep your orders across devices.</p>
              <button onClick={() => navigate("/login")} className="mt-2 text-sm font-semibold text-metros-red">Sign In</button>
            </div>
          )}
        </div>
      ) : !user ? (
        <div className="text-center py-16" data-testid="orders-guest">
          <ClipboardList className="mx-auto text-gray-300" size={40} />
          <p className="font-heading font-bold text-xl mt-3">Track your order above</p>
          <p className="text-muted-foreground text-sm mt-1">Enter your order number, or sign in to save your order history.</p>
          <button onClick={() => navigate("/login")} className="mt-4 bg-metros-red text-white rounded-full px-6 py-3 font-semibold">Sign In</button>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16" data-testid="no-orders">
          <ClipboardList className="mx-auto text-gray-300" size={40} />
          <p className="font-heading font-bold text-xl mt-3">No orders yet</p>
          <p className="text-muted-foreground text-sm">Your past orders will appear here.</p>
          <button onClick={() => navigate("/menu")} className="mt-4 bg-metros-red text-white rounded-full px-6 py-3 font-semibold">Start Ordering</button>
        </div>
      ) : null}
      {chatOrder && <OrderChatModal order={chatOrder} viewer="customer" onClose={() => setChatOrder(null)} />}
    </div>
  );
}
